import exactMath from "exact-math";
import { WEEKDAYS } from "lib/date";

import {
  getBasketItemByID,
  getSchoolIDList,
  isDinnersEnabled,
  isDinnerTopUpsEnabled,
  doesBasketItemExist
} from "state/school_money/selectors";

/**
 * @returns {Immutable.List}
 */
export function getDinnerBookingIDList(state) {
  return state.getIn(["school_money", "dinnerBookings"]).keySeq().toList();
}

/**
 * @returns {boolean}
 */
export function doesDinnerBookingExist(state, dinnerBookingID) {
  return getDinnerBookingIDList(state).includes(dinnerBookingID);
}

/**
 * @returns {Immutable.List}
 */
export function getSchoolDinnerBookingIDList(state, schoolID) {
  return state
    .getIn(["school_money", "dinnerBookings"])
    .filter(dinnerBooking => dinnerBooking.get("schoolID") === schoolID)
    .sortBy(dinnerBooking => dinnerBooking.get("startDate"))
    .keySeq().toList();
}

/**
 * @returns {Immutable.List}
 */
export function getStudentDinnerBookingIDList(state, studentID) {
  return state
    .getIn(["school_money", "dinnerBookings"])
    .filter(dinnerBooking => dinnerBooking.get("studentID") === studentID)
    .sortBy(dinnerBooking => dinnerBooking.get("startDate"))
    .keySeq().toList();
}

/**
 * @returns {Immutable.Map}
 */
export function getDinnerBookingByID(state, dinnerBookingID) {
  return state.getIn(["school_money", "dinnerBookings", dinnerBookingID]);
}

/**
 * @returns {boolean}
 */
export function isDinnerBookingCashless(state, dinnerBookingID) {
  const dinnerBooking = getDinnerBookingByID(state, dinnerBookingID);
  return dinnerBooking.get("isCashless");
}


/**
 * @returns {number}
 */
export function getDinnerBookingTotalPaid(state, dinnerBookingID) {
  const dinnerBooking = getDinnerBookingByID(state, dinnerBookingID);
  return dinnerBooking.get("totalPaid");
}


/**
 * @returns {Immutable.Map}
 */
export function getDinnerBookingBookedOption(state, dinnerBookingID, weekdayIndex) {
  const dinnerBooking = getDinnerBookingByID(state, dinnerBookingID);
  return dinnerBooking.getIn(["bookedOptions", weekdayIndex]);
}

/**
 * Get the list of options available for a dinner booking day
 * @returns {Immutable.List}
 */
export function getDinnerBookingOptionList(state, dinnerBookingID, weekdayIndex) {
  const dinnerBooking = getDinnerBookingByID(state, dinnerBookingID);
  const availableOptions = dinnerBooking.getIn(["availableOptions", weekdayIndex]);
  const bookedOption = getDinnerBookingBookedOption(state, dinnerBookingID, weekdayIndex);
  // if an optionis already booked, use the version that was booked
  // this ensure that price changes do not affect existing bookings
  return availableOptions.map(option => {
    const isBooked = option.get("id") === bookedOption.get("id");
    return isBooked ? bookedOption : option;
  })
}

/**
 * Get the details of the dinner option with the given ID for a dinner booking day
 * @returns {(Immutable.Map|null)}
 */
export function getDinnerBookingOption(state, dinnerBookingID, weekdayIndex, optionID) {
  const optionList = getDinnerBookingOptionList(state, dinnerBookingID, weekdayIndex);
  const option = optionList.find(option => option.id === optionID);
  return option || null;
}

/**
 * @returns {Immutable.List}
 */
export function getDinnerBookingSelectedOptionIDList(state, dinnerBookingID, weekdayIndex) {
  const isInBasket = isDinnerBookingInBasket(state, dinnerBookingID);
  if (isInBasket) {
    const basketItem = getBasketItemByID(state, "dinnerBooking", dinnerBookingID);
    return basketItem.payload.get("optionIDs");
  } else {
    const dinnerBooking = getDinnerBookingByID(state, dinnerBookingID);
    return dinnerBooking.selectedOptionIDs;
  }
}

/**
 * @returns {string}
 */
export function getDinnerBookingSelectedOptionID(state, dinnerBookingID, weekdayIndex) {
  const optionIDs = getDinnerBookingSelectedOptionIDList(state, dinnerBookingID);
  return optionIDs.get(weekdayIndex);
}

/**
 * Get the selected option for a dinner booking day
 * @returns {Immutable.Map}
 */
export function getDinnerBookingSelectedOption(state, dinnerBookingID, weekdayIndex) {
  const selectedOptionID = getDinnerBookingSelectedOptionID(state, dinnerBookingID, weekdayIndex);
  return getDinnerBookingOption(state, dinnerBookingID, weekdayIndex, selectedOptionID);
}

/**
 * Get a list of the options selected per day for a dinner booking
 * @returns {Array<Immutable.Map>}
 */
function getDinnerBookingSelectedOptions(state, dinnerBookingID) {
  return WEEKDAYS
    .map((_, weekdayIndex) => getDinnerBookingSelectedOption(state, dinnerBookingID, weekdayIndex));
}

/**
 * Get the total price of all selections in a dinner booking
 * @returns {number}
 */
export function getDinnerBookingTotalPrice(state, dinnerBookingID) {
  return getDinnerBookingSelectedOptions(state, dinnerBookingID)
    .filter(option => option !== null)
    .map(option => option.price)
    .reduce((totalPrice, optionPrice) => exactMath.add(totalPrice, optionPrice), 0);
}

/**
 * Get the "to pay" price of a dinner booking
 * This is basically the total price minus anything already paid
 * @returns {number}
 */
export function getDinnerBookingBasketPrice(state, dinnerBookingID) {
  const totalPrice = getDinnerBookingTotalPrice(state, dinnerBookingID);
  const totalPaid = getDinnerBookingTotalPaid(state, dinnerBookingID);
  return Math.max(0, exactMath.sub(totalPrice, totalPaid));
}

/**
 * Get the per-day allergy information for the selected options in a dinner booking
 * @returns {Array<string>}
 */
export function getDinnerBookingAllergyInformation(state, dinnerBookingID) {
  return getDinnerBookingSelectedOptions(state, dinnerBookingID)
    .map(option => {
      if (option === null) {
        return "";
      }
      return option.get("allergyInformation", "");
    });
}

/**
 * @returns {boolean}
 */
export function doesDinnerBookingHaveAllergyInformation(state, dinnerBookingID) {
  return getDinnerBookingAllergyInformation(state, dinnerBookingID)
    .some(allergyInformation => allergyInformation !== "");
}

/**
 * @returns {boolean}
 */
export function isDinnerBookingDayFSMEligible(state, dinnerBookingID, weekdayIndex) {
  const dinnerBooking = getDinnerBookingByID(state, dinnerBookingID);
  return dinnerBooking.fsmEligibility.get(weekdayIndex);
}

/**
 * @returns {boolean}
 */
export function doesDinnerBookingHaveFSMEligibility(state, dinnerBookingID) {
  return WEEKDAYS
    .some((_, weekdayIndex) => isDinnerBookingDayFSMEligible(state, dinnerBookingID, weekdayIndex));
}

/**
 * @returns {boolean}
 */
export function isDinnerBookingDayHoliday(state, dinnerBookingID, weekdayIndex) {
  const dinnerBooking = getDinnerBookingByID(state, dinnerBookingID);
  return dinnerBooking.getIn(["holidays", weekdayIndex]);
}

/**
 * Has the dinner booking had it's selections changed from what is on the server?
 * @returns {boolean}
 */
export function hasDinnerBookingBeenModified(state, dinnerBookingID) {
  return WEEKDAYS.some((_, weekdayIndex) => {
    const selectedOptionID = getDinnerBookingSelectedOptionID(state, dinnerBookingID, weekdayIndex);
    const bookedOption = getDinnerBookingBookedOption(state, dinnerBookingID, weekdayIndex);
    return selectedOptionID !== bookedOption.get("id");
  });
}

/**
 * @returns {boolean}
 */
export function isDinnersReady(state) {
  return !state.getIn(["school_money", "isActivePaymentsLoading"]);
}

/**
 * @returns {boolean}
 */
export function isAllergiesWarningEnabled(state) {
  return state.getIn(["school_money", "isAllergiesWarningEnabled"]);
}

/**
 * @returns {boolean}
 */
export function isDinnerBookingExpanded(state, dinnerBookingID) {
  return state.getIn(["school_money", "expandedDinnerBookingID"]) === dinnerBookingID;
}

/**
 * @returns {boolean}
 */
export function isDinnerBookingInBasket(state, dinnerBookingID) {
  return doesBasketItemExist(state, "dinnerBooking", dinnerBookingID);
}

/**
 * @returns {boolean}
 */
export function isDinnerBookingSeen(state, dinnerBookingID) {
  return state.getIn(["school_money", "seenDinnerBookingIDs"]).includes(dinnerBookingID);
}

/**
 * @returns {number}
 */
export function getNewDinnerBookingCount(state) {
  // filter out schools that don't use dinner bookings
  const schoolIDList = getSchoolIDList(state)
    .filter(schoolID => isDinnersEnabled(state, schoolID))
    .filterNot(schoolID => isDinnerTopUpsEnabled(state, schoolID));
  return schoolIDList
    .map(schoolID => getSchoolDinnerBookingIDList(state, schoolID))
    .flatten(1)
    .filterNot(dinnerBookingID => isDinnerBookingSeen(state, dinnerBookingID))
    .count();
}

/**
 * @returns {string}
 */
export function getGoToDinnerBookingID(state) {
  return state.getIn(["school_money", "goToDinnerBookingID"]);
}

/**
 * @returns {date}
 */
export function getDinnerBookingDayDate(state, dinnerBookingID, weekdayIndex) {
  const dinnerBooking = getDinnerBookingByID(state, dinnerBookingID);
  return dinnerBooking.dates.get(weekdayIndex);
}

/**
 * @returns {boolean}
 */
export function isDinnerBookingDayExpired(state, dinnerBookingID, weekdayIndex) {
  const dinnerBooking = getDinnerBookingByID(state, dinnerBookingID);
  return dinnerBooking.expired.get(weekdayIndex);
}

/**
 * @returns {boolean}
 */
export function doesDinnerBookingHaveBookings(state, dinnerBookingID) {
  const dinnerBooking = getDinnerBookingByID(state, dinnerBookingID);
  return dinnerBooking.bookedOptions
    .some(({ id }) => id !== -1 && id !== "NONE" && id !== "");
}



// WEBPACK FOOTER //
// ./src/state/school_money/selectors/dinners.js